import { useCallback, useContext, useState } from 'react';

import { checkDocumentSharpness, putDocuments } from 'PrivateAPI';

import { ConfigContext } from 'Context/config';
import { useUserPartial } from 'Context/user';

import { DocumentUploadSource } from '../DocumentCapture.helpers';
import { captureSentryException } from '../DocumentCaptureError/captureSentryException';
import { displayToast } from '../DocumentCaptureError/displayToast';
import { UploadDocumentError } from '../DocumentCaptureError/UploadDocumentError';

// Duplicate code of src/shared/containers/app/vehicleProfiling/ServiceHistory/upload/hooks.jsx
const useXHRUpload = (api: string) => {
  const { auth_token: authToken, cognitoTokens } = useUserPartial();
  const {
    configState: { appName, appVersion },
  } = useContext(ConfigContext);
  const [loading, setLoading] = useState<null | boolean>(null);
  const [percent, setPercent] = useState<null | number>(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState<Record<string, unknown> | null>(null);

  const upload = useCallback((data: FormData) =>
    new Promise<Record<string, unknown>>((resolve, reject) => {
      setError(null);
      setPercent(0);
      setLoading(true);

      const request = new XMLHttpRequest();
      const setErrorHandler = () => {
        const renderErrorMessage = (msg: XMLHttpRequest['response']) => {
          if (msg.includes('Mimetype is not supported')) {
            return 'Image format is not supported';
          }

          return msg;
        };
        const response = {
          error: renderErrorMessage(request.response),
          status: request.status,
        };

        setError(response);
        reject(response);
      };
      request.open('POST', api, true);

      if (cognitoTokens?.idToken) {
        request.setRequestHeader('x-mway-authorization', cognitoTokens.idToken);
      } else if (authToken) {
        request.setRequestHeader('x-access-token', authToken);
      }

      request.setRequestHeader('x-mway-version', appVersion || '');
      request.setRequestHeader('x-mway-app', appName || '');
      request.setRequestHeader('Request-Id', `${appName}-${appVersion}-${data.get('enquiryId')}-${data.get('kind')}-${Date.now()}`);

      request.upload.addEventListener('progress', (e) => {
        const newPercent = (e.loaded / e.total) * 100;
        // This conversion doesn't care if newPercent is a string or a number,
        // the toString call will always return a string and parseInt will always
        // return a number.
        const typeSafePercent = parseInt(newPercent.toString(), 10);
        setPercent(typeSafePercent);
      });
      request.addEventListener('load', async () => {
        if (request.status === 200) {
          const response = JSON.parse(request.response);
          setResult(response);
          resolve(response);
        } else {
          setErrorHandler();
        }
        setLoading(false);
        setPercent(null);
      });

      request.addEventListener('error', () => {
        setErrorHandler();
        setLoading(false);
        setPercent(null);
      });

      request.send(data);
    }), [api, appName, appVersion, cognitoTokens?.idToken, authToken]);

  return {
    error, loading, percent, result, upload,
  };
};

const useUpload = (
  { onError = () => {}, onSuccess }:
  { onError: (err: unknown) => void; onSuccess: (arg: Record<string, unknown>) => void },
) => {
  const {
    configState: { photoUploader },
  } = useContext(ConfigContext);

  const [isLoading, setIsLoading] = useState(false);

  const {
    error, result, upload,
  } = useXHRUpload(putDocuments(photoUploader));

  const objToFormData = (dataset: [string, string | Blob][]) => dataset.reduce((formData, args) => {
    formData.append(...args);
    return formData;
  }, new FormData());

  const doUpload = async ({
    enquiryId,
    file,
    id,
    isReupload,
    kind,
    label,
    pageNumber,
    source,
  }: {
    enquiryId?: string;
    file: File;
    id?: number;
    isReupload: boolean;
    kind: string;
    label: string;
    pageNumber: number;
    source: DocumentUploadSource;
  }) => {
    setIsLoading(true);

    // Convert all non-string values to strings, as this will happen in FormData.append anyway
    // See MSDN: https://developer.mozilla.org/en-US/docs/Web/API/FormData/append
    // value.toString() will cause an error if value is null or undefined. String(value) will not.
    const dataArray: [string, string | Blob][] = [
      ['enquiryId', String(enquiryId)],
      ['kind', kind],
      ['file', file],
      ['name', `${enquiryId}-${kind}-default-${Date.now()}.${file.name.split('.').pop()}`],
      ['pageNumber', String(pageNumber)],
    ];

    if (isReupload) {
      dataArray.push(['id', String(id)]);
      dataArray.push(['isReupload', String(isReupload)]);
      dataArray.push(['type', 'default']);
    }

    if (source) {
      dataArray.push(['source', source]);
    }

    const formData = objToFormData(dataArray);

    try {
      let isSharp = true;
      let sharpness;
      const { id: documentId, location } = await upload(formData);

      try {
        const res = await checkDocumentSharpness({ documentId });
        isSharp = res.isSharp;
        sharpness = res.sharpness;
      } catch (err) {
        displayToast(err);
        captureSentryException(err);
      }

      onSuccess({
        documentId, file, isSharp, kind, label, pageNumber, sharpness, url: location,
      });
    } catch (e) {
      onError(new UploadDocumentError(UploadDocumentError.getErrorMessage(e)));
    }

    setIsLoading(false);
  };

  return {
    error, loading: isLoading, result, upload: doUpload,
  };
};

// eslint-disable-next-line import/no-default-export
export default useUpload;
